<template>
  <Teleport
    :to="teleportTo"
    :class="{ 'd-contents': disableTeleport }"
    :disabled="id === '__static' || disableTeleport"
  >
    <TransitionGroup
      name="scale"
    >
      <div
        key="overlay"
        :class="visibleClass"
        class="overlay p-fixed"
        @click.stop="toggleModalVisibility(id, false)"
      />

      <div
        :id="id"
        key="modal"
        v-bind="$attrs"
        :class="visibleClass"
        class="modal p-fixed border-radius-2"
      >
        <div class="modal__wrapper border-radius-2 py-3 px-6">
          <div
            data-grid
            class="modal__header grid--va-center grid--between mb-5"
          >
            <h4 class="modal__title m-0 text-uppercase overflow-hidden">
              <slot name="modal-title">
                {{ title }}
              </slot>
            </h4>

            <span
              v-if="closeable"
              data-grid
              class="modal__closer grid--va-center grid--center cursor-pointer"
              @click.stop="toggleModalVisibility(id, false)"
            >
              <CloserIcon />
            </span>
          </div>

          <div class="modal__body">
            <slot
              name="modal-content"
              :visible="isOpened"
              :toggle="toggleModalVisibility"
            />
          </div>
        </div>
      </div>
    </TransitionGroup>
  </Teleport>
</template>

<script>
import { defineComponent } from 'vue'

import CloserIcon from '../../icons/CloserIcon'

export default defineComponent({
  name: 'Modal',

  components: {
    CloserIcon
  },

  props: {
    closeable: {
      type: Boolean,
      default: true
    },

    id: {
      type: String,
      required: true
    },

    title: {
      type: String,
      default: ''
    },

    clearAfterCloser: {
      type: Boolean
    },

    teleportTo: {
      type: String,
      default: () => '#app #modals'
    },

    disableTeleport: Boolean
  },

  emits: ['modal-status'],

  data () {
    return {
      isOpened: false
    }
  },

  computed: {
    visibleClass () {
      return {
        visible: this.isOpened || this.id === '__static'
      }
    }
  },

  watch: {
    isOpened (v) {
      if (v) {
        document.body.addEventListener('keyup', this.watchKeyboard, false)

        return
      }

      document.body.removeEventListener('keyup', this.watchKeyboard, false)
    }
  },

  mounted () {
    this.emitter.on('toggle-modal', this.toggleModalVisibility)
    this.emitter.emit('modal-ready', this.id)
  },

  unmounted () {
    this.emitter.off('toggle-modal', this.toggleModalVisibility)
  },

  methods: {
    toggleModalVisibility (id, force = null) {
      if (!this.closeable || this.id !== id) return

      this.isOpened = force ?? !this.isOpened

      this.$emit('modal-status', this.isOpened)

      if (this.clearAfterCloser) this.emitter.emit('clear-all-inputs')
    },

    watchKeyboard (e) {
      const { isTrusted, keyCode } = e

      if (!isTrusted) return

      if (keyCode === 27 && this.isOpened) {
        this.toggleModalVisibility(this.id, false)
      }
    }
  }
})
</script>

<style lang="scss" scoped>
  .modal,
  .overlay {
    opacity: 0;
    visibility: hidden;

    &.visible {
      visibility: visible;
      opacity: var(--opacityCount);
    }
  }

  .overlay {
    --opacityCount: .5;

    inset: 0;
    z-index: 998;
    background-color: rgb(var(--black));

    transition: visibility 0s ease-in-out .8s, opacity .4s ease-in-out .4s;

    &.visible {
      transition: visibility 0s, opacity .4s ease-in;
    }
  }

  .modal {
    --opacityCount: 1;

    z-index: 999;
    top: 40%;
    left: 50%;
    max-height: 90vh;
    max-width: min(100%, 945px);
    transform: translate(-50%, -50%);
    transition: visibility 0s ease-in-out .4s, opacity .4s ease-in-out, top .4s ease-in-out;

    &.visible {
      top: 50%;
      transition: visibility 0s, opacity .4s ease-in, top .4s ease-in-out;
    }

    &__wrapper {
      background-color: rgb(var(--white));

      .is-dark-theme & {
        background-color: rgb(var(--black1));
      }
    }

    &__header {
      height: 35px;
    }

    &__title {
      white-space: nowrap;
      text-overflow: ellipsis;

      color: var(--primary);
      font: 700 1.4rem "Archivo", sans-serif;
    }

    &__closer {
      width: 20px;
      height: 20px;
      flex: 0 0 20px;

      .is-dark-theme & {
        color: rgb(var(--white));
      }

      svg {
        width: 75%;
        height: 75%;
        color: rgb(var(--gray1));
      }
    }

    :deep(.defaultbutton) {
      background-color: var(--primary);
    }
  }
</style>
